// Motion pieces for the marketing pages: rotating hero word, marquee, and the large
// "bento" boxes that frame a drawing or mock of the product.
import { useEffect, useState, type ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SmartLink } from "@/components/Header";
import { integrations } from "@/data/site";

const isPrerender = () => typeof navigator !== "undefined" && /HeadlessChrome/.test(navigator.userAgent);

/** Steps 0..count-1 on a timer, but only while `active`. */
function useLoop(count: number, ms: number, active: boolean) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setI((v) => (v + 1) % count), ms);
    return () => clearInterval(t);
  }, [count, ms, active]);
  return i;
}

/* ── Hero: a word that changes inside a soft pill ─────────────────────────── */
export const RotatingWord = ({ words }: { words: string[] }) => {
  const reduce = useReducedMotion();
  const i = useLoop(words.length, 2200, !reduce && !isPrerender());
  const longest = words.reduce((a, b) => (b.length > a.length ? b : a), "");
  return (
    <span className="relative mx-[0.12em] inline-flex items-center gap-[0.22em] rounded-[0.5em] bg-[hsl(216_90%_95%)] px-[0.32em] py-[0.02em] align-baseline">
      <span className="h-[0.34em] w-[0.34em] shrink-0 rounded-full bg-[hsl(216_84%_52%)]" />
      <span className="relative inline-block overflow-hidden text-left font-semibold tracking-[-0.03em]">
        <span className="invisible block">{longest}</span>
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={words[i]}
            className="absolute inset-0"
            initial={{ y: "0.55em", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "-0.55em", opacity: 0 }}
            transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
          >
            {words[i]}
          </motion.span>
        </AnimatePresence>
      </span>
    </span>
  );
};

/* ── Marquee ──────────────────────────────────────────────────────────────── */
export const Marquee = ({ children, speed = 38 }: { children: ReactNode; speed?: number }) => (
  <div className="cm-marquee relative overflow-hidden" style={{ ["--cm-marquee-s" as string]: `${speed}s` }}>
    <div className="cm-marquee-track flex w-max items-center gap-12 pr-12">
      {children}
      <span aria-hidden className="contents">{children}</span>
    </div>
  </div>
);

export const IntegrationMarquee = () => (
  <Marquee>
    {integrations.map((i) => (
      <span key={i.key} className="flex shrink-0 items-center gap-2.5 text-[17px] font-semibold tracking-[-0.01em] text-foreground">
        <img src={i.logo} alt="" className="h-6 w-6 object-contain" loading="lazy" /> {i.name}
      </span>
    ))}
  </Marquee>
);

/* The apps Inbox connects to, still: a short list reads better standing still. */
export const IntegrationRow = () => (
  <ul className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
    {integrations.map((i) => (
      <li key={i.key}>
        <a href={i.href ?? "/integrations"} className="group flex items-center gap-2.5 text-[17px] font-semibold tracking-[-0.01em] text-foreground">
          <img src={i.logo} alt="" className="h-6 w-6 object-contain" loading="lazy" /> {i.name}
        </a>
      </li>
    ))}
  </ul>
);

/* ── Bento frame ──────────────────────────────────────────────────────────── */
export const Bento = ({
  eyebrow,
  title,
  href,
  children,
  wide = false,
}: {
  eyebrow: string;
  title: string;
  href: string;
  children: ReactNode;
  wide?: boolean;
}) => (
  <SmartLink
    href={href}
    className={`group relative flex overflow-hidden rounded-2xl bg-[hsl(40_9%_96%)] transition-colors duration-200 hover:bg-[hsl(40_9%_94%)] ${
      wide ? "flex-col lg:flex-row lg:items-stretch" : "flex-col"
    }`}
  >
    <div className={`flex items-start justify-between gap-4 p-6 sm:p-7 ${wide ? "lg:w-[38%] lg:flex-col lg:justify-start" : ""}`}>
      <div>
        <p className="text-[15px] text-foreground">{eyebrow}</p>
        <h3 className="mt-1.5 text-[1.35rem] font-semibold leading-snug tracking-[-0.02em] text-foreground sm:text-2xl">{title}</h3>
      </div>
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-foreground text-background transition-transform duration-200 group-hover:translate-x-0.5 ${wide ? "lg:mt-5" : ""}`}>
        <ArrowRight className="h-4 w-4" />
      </span>
    </div>
    <div className={`relative min-h-[340px] flex-1 ${wide ? "lg:min-h-[360px]" : ""}`}>{children}</div>
  </SmartLink>
);

/* ── Use-case cards: round colour icon, title, arrow ───────────────────────── */
export const UseCaseCard = ({ icon, tint, title, href }: { icon: ReactNode; tint: string; title: string; href: string }) => (
  <SmartLink href={href} className="cm-card cm-card-hover group flex h-full flex-col p-6">
    <span className={`flex h-10 w-10 items-center justify-center rounded-full text-white transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-105 ${tint}`}>{icon}</span>
    <span className="mt-7 text-[17px] font-semibold leading-snug tracking-[-0.01em] text-foreground">
      {title} <ArrowRight className="inline h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
    </span>
  </SmartLink>
);
